import type { Commitment, FormatItem, Frequency, GlobalConfig, MaterialItem, MaterialSelection, Transportation, Unit } from '@generated/types';
import { formatWeight } from '@generated/utils/units';

export const DEFAULT_FILL_WEIGHT_LBS = 500;

export function shipmentsPerYear(frequency: Frequency): number {
  if (frequency === 'Monthly') return 12;
  if (frequency === 'Quarterly') return 4;
  return 1;
}

export function cadencePhrase(frequency: Frequency): string {
  if (frequency === 'Monthly') return 'a month';
  if (frequency === 'Quarterly') return 'per shipment, four times a year';
  return 'in a single annual shipment';
}

export const VALID_COMMITMENTS_BY_FREQUENCY: Record<Frequency, Commitment[]> = {
  Monthly: ['Month-to-Month', '6 months', '12 months', '24 months', '36 months', '48 months'],
  Quarterly: ['6 months', '12 months', '24 months', '36 months', '48 months'],
  Annual: ['12 months', '24 months', '36 months', '48 months'],
};

export function singularize(name: string): string {
  return name.endsWith('s') ? name.slice(0, -1) : name;
}

export function pluralizeCount(count: number, name: string): string {
  return count === 1 ? singularize(name) : name;
}

/** Weighted-average fill weight for volumetric formats; nominal fill for fixed formats. */
export function computeFillWeightLbs(
  selectedFormat: FormatItem | undefined,
  materials: MaterialItem[],
  selections: Record<string, MaterialSelection>,
  global: GlobalConfig | null,
): number {
  if (!selectedFormat) return DEFAULT_FILL_WEIGHT_LBS;
  if (!selectedFormat.volumetric) {
    return selectedFormat.nominalFillLbs ?? DEFAULT_FILL_WEIGHT_LBS;
  }
  let weightedSum = 0;
  let totalLbs = 0;
  for (const m of materials) {
    const sel = selections[m.id];
    if (sel?.checked && sel.weightLbs && m.fillLbs != null) {
      weightedSum += m.fillLbs * sel.weightLbs;
      totalLbs += sel.weightLbs;
    }
  }
  if (totalLbs <= 0) return DEFAULT_FILL_WEIGHT_LBS;
  let fill = weightedSum / totalLbs;
  if (global?.volumetricFillMinLbs != null) fill = Math.max(fill, global.volumetricFillMinLbs);
  if (global?.volumetricFillMaxLbs != null) fill = Math.min(fill, global.volumetricFillMaxLbs);
  return fill;
}

// The entered weight is already the amount for ONE shipment cycle (whatever the chosen
// frequency's cycle is) — no further scaling by month count is needed here.
export function computeUnitsPerShipment(perShipmentLbs: number, fillWeightLbs: number): number {
  if (fillWeightLbs <= 0 || perShipmentLbs <= 0) return 0;
  return Math.ceil(perShipmentLbs / fillWeightLbs);
}

export function computeAnnualUnits(unitsPerShipment: number, frequency: Frequency): number {
  return unitsPerShipment * shipmentsPerYear(frequency);
}

/** Decide the formula by which Rate Card field is populated, never by format name. */
export function computeAnnualCost(format: FormatItem | undefined, annualUnits: number, annualLbs: number): number | null {
  if (!format) return null;
  if (format.handlingPerUnit != null) return annualUnits * format.handlingPerUnit;
  if (format.pricePerTon != null) return (annualLbs / 2000) * format.pricePerTon;
  return null;
}

/**
 * Period cost = annual cost / shipments per year: /12 for Monthly, /4 for Quarterly, /1 for
 * One-Time/Annually. Dividing by 1 for Annual would just reproduce the annual cost, so there is
 * no distinct sub-annual figure for that frequency — return null so every caller (summary panel,
 * confirmation page, PDF, and the board's Est Period Cost field) leaves that figure blank and
 * hides the cell entirely instead of showing a redundant value or an empty dash.
 */
export function computePeriodCost(annualCost: number | null, frequency: Frequency): number | null {
  if (annualCost == null) return null;
  if (frequency === 'Annual') return null;
  return annualCost / shipmentsPerYear(frequency);
}

export function periodCostLabel(frequency: Frequency, isHandling: boolean): string | null {
  const noun = isHandling ? 'handling' : 'cost';
  if (frequency === 'Monthly') return `Estimated monthly ${noun}`;
  if (frequency === 'Quarterly') return `Estimated quarterly ${noun}`;
  return null;
}

export function annualCostLabel(isHandling: boolean): string {
  return `Estimated annual ${isHandling ? 'handling' : 'cost'}`;
}

export function buildSizingLine(
  perShipmentLbs: number,
  unit: Unit,
  frequency: Frequency,
  format: FormatItem | undefined,
  unitsPerShipment: number,
  fillWeightLbs: number,
): string {
  if (perShipmentLbs <= 0 || !format) {
    return "Enter your weights above and we'll size the shipment for you.";
  }
  const shipmentDisplay = formatWeight(perShipmentLbs, unit);
  const noun = pluralizeCount(unitsPerShipment, format.name);
  let line = `At ${shipmentDisplay} ${unit} per shipment, that's about ${unitsPerShipment} ${noun} ${cadencePhrase(frequency)}`;
  if (format.volumetric) {
    return `${line}, figuring about ${Math.round(fillWeightLbs)} lbs in each based on what you're sending.`;
  }
  return `${line}.`;
}

export function buildBoxCeilingNote(global: GlobalConfig | null, collectionBoxFormat: FormatItem | undefined): string | null {
  if (!global?.boxMaxMonthlyLbs || !collectionBoxFormat?.nominalFillLbs) return null;
  const maxBoxes = Math.round(global.boxMaxMonthlyLbs / collectionBoxFormat.nominalFillLbs);
  return `Over ${Math.round(global.boxMaxMonthlyLbs).toLocaleString()} lbs a month, Collection Boxes stop making sense — that is more than ${maxBoxes} boxes. Gaylords, bulk bags or bales are the better fit.`;
}

export function frequencyHelperText(frequency: Frequency | null): string | null {
  if (frequency && frequency !== 'Monthly') return 'Annual volume stays the same; each shipment is just larger.';
  return null;
}

export function transportationHelperText(format: FormatItem | undefined, transportation: Transportation | null): string | null {
  if (format?.name === 'Collection Boxes') return 'Round-trip shipping is included in the Collection Box plan.';
  if (transportation === 'ByFusion arranges') return 'Transportation will be calculated separately and finalized in the service agreement.';
  if (transportation === 'Partner arranges') {
    return "You'll arrange transportation to ByFusion. Any handling or other applicable rates are finalized in the service agreement.";
  }
  return null;
}

export function formatNoteText(format: FormatItem | undefined): string | null {
  if (!format) return null;
  return format.palletRequired ? 'Shipped on pallets.' : 'Round-trip shipping and pickup included.';
}

export function palletNote(format: FormatItem | undefined): string | null {
  return format?.palletRequired ? 'Gaylords, bulk bags and bales must be shipped on pallets.' : null;
}

export function isValidHttpUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}
